import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { meetingsAPI, casesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Plus, Clock, Video, Trash2, Loader2, CheckCircle, XCircle, Send
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const MEETING_TYPES = [
  { value: 'initial_consultation', label: 'Initial Consultation' },
  { value: 'document_review', label: 'Document Review' },
  { value: 'case_status_update', label: 'Case Status Update' },
  { value: 'biometrics_prep', label: 'Biometrics Preparation' },
  { value: 'interview_prep', label: 'Interview Preparation' },
  { value: 'custom', label: 'Custom Meeting' },
];

export default function CalendarPage() {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [videoPlatforms, setVideoPlatforms] = useState({ zoom: false });
  const [manualLinkMeetingId, setManualLinkMeetingId] = useState(null);
  const [manualLink, setManualLink] = useState('');
  const [formData, setFormData] = useState({
    case_id: '', title: '', meeting_type: 'custom', description: '',
    date: '', start_time: '', end_time: '', location: '', is_virtual: true, video_platform: 'none',
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [meetingsRes, casesRes, videoRes] = await Promise.all([
        meetingsAPI.getAll(),
        casesAPI.getAll(),
        meetingsAPI.getVideoPlatformStatus().catch(() => ({ data: { zoom: false } })),
      ]);
      setMeetings(meetingsRes.data || []);
      setCases(casesRes.data || []);
      setVideoPlatforms(videoRes.data || { zoom: false });
    } catch (e) { toast.error('Failed to load data'); }
    setLoading(false);
  };

  const createMeeting = async (e) => {
    e.preventDefault();
    if (!formData.case_id || !formData.title || !formData.date || !formData.start_time || !formData.end_time) {
      toast.error('Please fill all required fields'); return;
    }
    try {
      await meetingsAPI.create({
        ...formData,
        start_time: `${formData.date}T${formData.start_time}:00`,
        end_time: `${formData.date}T${formData.end_time}:00`,
      });
      toast.success('Meeting created');
      setIsCreateOpen(false);
      setFormData({ case_id: '', title: '', meeting_type: 'custom', description: '', date: '', start_time: '', end_time: '', location: '', is_virtual: true, video_platform: 'none' });
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteMeeting = async (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    try { await meetingsAPI.delete(id); toast.success('Deleted'); loadData(); } 
    catch (e) { toast.error('Failed'); }
  };

  const updateStatus = async (id, status) => {
    try { await meetingsAPI.update(id, { status }); toast.success('Updated'); loadData(); }
    catch (e) { toast.error('Failed'); }
  };

  const statusBadge = (s) => {
    const styles = { scheduled: 'bg-blue-100 text-blue-800', completed: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800', reschedule_requested: 'bg-yellow-100 text-yellow-800' };
    return <Badge className={styles[s] || 'bg-gray-100'}>{s?.replace('_', ' ')}</Badge>;
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const formatTime = (d) => new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const isSameDay = (d1, d2) => new Date(d1).toDateString() === new Date(d2).toDateString();
  const selectedMeetings = meetings.filter(m => isSameDay(m.start_time, selectedDate));

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />

      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        <div className="flex items-center justify-between mb-8">
          <div><h1 className="text-3xl font-heading font-bold">Calendar</h1><p className="text-muted-foreground">Schedule and manage client meetings</p></div>
          <div className="flex items-center gap-3">
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild><Button className="bg-primary text-white" data-testid="create-meeting-btn"><Plus className="w-4 h-4 mr-2" />Schedule Meeting</Button></DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Schedule New Meeting</DialogTitle></DialogHeader>
                <form onSubmit={createMeeting} className="space-y-4">
                  <div><Label>Client/Case *</Label><Select value={formData.case_id} onValueChange={(v) => setFormData(p => ({ ...p, case_id: v }))}><SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger><SelectContent>{cases.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} placeholder="Meeting title" /></div>
                    <div><Label>Type</Label><Select value={formData.meeting_type} onValueChange={(v) => setFormData(p => ({ ...p, meeting_type: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{MEETING_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
                  </div>
                  <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Meeting details..." /></div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>Date *</Label><Input type="date" value={formData.date} onChange={(e) => setFormData(p => ({ ...p, date: e.target.value }))} /></div>
                    <div><Label>Start *</Label><Input type="time" value={formData.start_time} onChange={(e) => setFormData(p => ({ ...p, start_time: e.target.value }))} /></div>
                    <div><Label>End *</Label><Input type="time" value={formData.end_time} onChange={(e) => setFormData(p => ({ ...p, end_time: e.target.value }))} /></div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Switch checked={formData.is_virtual} onCheckedChange={(v) => setFormData(p => ({ ...p, is_virtual: v, video_platform: v ? p.video_platform : 'none' }))} /><Label>{formData.is_virtual ? 'Virtual' : 'In-Person'}</Label>
                  </div>
                  {formData.is_virtual && (
                    <div>
                      <Label>Video Platform</Label>
                      <Select value={formData.video_platform} onValueChange={(v) => setFormData(p => ({ ...p, video_platform: v, location: v === 'custom' ? p.location : '' }))}>
                        <SelectTrigger data-testid="video-platform-select"><SelectValue placeholder="Select platform" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="zoom">Zoom {!videoPlatforms.zoom && '(Not configured)'}</SelectItem>
                          <SelectItem value="custom">Custom Link</SelectItem>
                          <SelectItem value="none">No Video Link</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {formData.is_virtual && formData.video_platform === 'custom' && (
                    <div><Label>Video Link</Label><Input value={formData.location} onChange={(e) => setFormData(p => ({ ...p, location: e.target.value }))} placeholder="https://zoom.us/j/... or custom video link" data-testid="video-link-input" /></div>
                  )}
                  {!formData.is_virtual && (
                    <div><Label>Location</Label><Input value={formData.location} onChange={(e) => setFormData(p => ({ ...p, location: e.target.value }))} placeholder="Office address" /></div>
                  )}
                  <div className="flex justify-end gap-3 pt-4 border-t"><Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button><Button type="submit" className="bg-primary text-white">Schedule</Button></div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardContent className="p-6">
              <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} className="rounded-md w-full" />
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" />{selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</CardTitle></CardHeader>
            <CardContent>
              {selectedMeetings.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No meetings scheduled</p>
              ) : (
                <div className="space-y-4">
                  {selectedMeetings.map((m) => (
                    <div key={m.id} className="p-4 border rounded-lg" data-testid={`meeting-${m.id}`}>
                      <div className="flex items-start justify-between mb-2"><h4 className="font-semibold">{m.title}</h4>{statusBadge(m.status)}</div>
                      <p className="text-sm text-muted-foreground mb-2">{m.case_name}</p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{formatTime(m.start_time)} - {formatTime(m.end_time)}</span>
                        {m.is_virtual && <Video className="w-4 h-4" />}
                      </div>
                      {m.status === 'scheduled' && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {m.video_link ? (
                            <a href={m.video_link} target="_blank" rel="noopener noreferrer">
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`start-meeting-${m.id}`}><Video className="w-4 h-4 mr-1" />Start Meeting</Button>
                            </a>
                          ) : m.is_virtual && m.video_platform && m.video_platform !== 'none' && (
                            manualLinkMeetingId === m.id ? (
                              <div className="flex gap-1 items-center w-full">
                                <Input placeholder="Paste Zoom or video link" value={manualLink} onChange={e => setManualLink(e.target.value)} className="h-8 text-xs" data-testid={`manual-link-input-${m.id}`} />
                                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-8 px-2" disabled={!manualLink} onClick={async () => {
                                  try { await meetingsAPI.setVideoLink(m.id, manualLink); toast.success('Link saved!'); setManualLinkMeetingId(null); setManualLink(''); loadData(); } catch (err) { toast.error('Failed to save link'); }
                                }}><Send className="w-3 h-3" /></Button>
                              </div>
                            ) : (
                              <div className="flex gap-1">
                                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`generate-link-${m.id}`} onClick={async () => {
                                  try {
                                    const res = await meetingsAPI.generateLink(m.id);
                                    toast.success('Meeting link generated!');
                                    loadData();
                                    window.open(res.data.video_link, '_blank');
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Could not generate link');
                                    setManualLinkMeetingId(m.id);
                                  }
                                }}><Video className="w-4 h-4 mr-1" />Generate & Start</Button>
                                <Button size="sm" variant="outline" onClick={() => setManualLinkMeetingId(m.id)} title="Add link manually" data-testid={`manual-link-btn-${m.id}`}><Send className="w-3 h-3" /></Button>
                              </div>
                            )
                          )}
                          <Button size="sm" variant="outline" onClick={() => updateStatus(m.id, 'completed')}><CheckCircle className="w-4 h-4 mr-1" />Complete</Button>
                          <Button size="sm" variant="outline" onClick={() => updateStatus(m.id, 'cancelled')}><XCircle className="w-4 h-4 mr-1" />Cancel</Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="mt-6">
          <CardHeader><CardTitle>All Meetings</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="w-full">
              <thead className="bg-secondary/50"><tr><th className="px-6 py-4 text-left text-xs font-semibold uppercase">Meeting</th><th className="px-6 py-4 text-left text-xs font-semibold uppercase">Client</th><th className="px-6 py-4 text-left text-xs font-semibold uppercase">Date & Time</th><th className="px-6 py-4 text-left text-xs font-semibold uppercase">Status</th><th className="px-6 py-4 text-right text-xs font-semibold uppercase">Actions</th></tr></thead>
              <tbody className="divide-y">
                {meetings.length === 0 && <tr><td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">No meetings scheduled</td></tr>}
                {meetings.map((m) => (
                  <tr key={m.id} className="hover:bg-secondary/30">
                    <td className="px-6 py-4"><p className="font-medium">{m.title}</p><p className="text-sm text-muted-foreground">{m.is_virtual ? '🎥 Virtual' : '📍 In-Person'}{m.video_platform && m.video_platform !== 'none' ? ` · ${m.video_platform === 'zoom' ? 'Zoom' : 'Custom'}` : ''}</p></td>
                    <td className="px-6 py-4">{m.case_name}</td>
                    <td className="px-6 py-4"><p>{formatDate(m.start_time)}</p><p className="text-sm text-muted-foreground">{formatTime(m.start_time)} - {formatTime(m.end_time)}</p></td>
                    <td className="px-6 py-4">{statusBadge(m.status)}</td>
                    <td className="px-6 py-4 text-right"><div className="flex items-center justify-end gap-2">{m.status === 'scheduled' && m.video_link && <a href={m.video_link} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm" className="text-blue-600 border-blue-300" data-testid={`join-meeting-${m.id}`}><Video className="w-4 h-4 mr-1" />Start</Button></a>}{m.status === 'scheduled' && !m.video_link && m.is_virtual && m.video_platform && m.video_platform !== 'none' && (manualLinkMeetingId === m.id ? <div className="flex gap-1 items-center"><Input placeholder="Paste link" value={manualLink} onChange={e => setManualLink(e.target.value)} className="h-8 text-xs w-48" /><Button size="sm" variant="outline" className="h-8 px-2" disabled={!manualLink} onClick={async () => { try { await meetingsAPI.setVideoLink(m.id, manualLink); toast.success('Link saved!'); setManualLinkMeetingId(null); setManualLink(''); loadData(); } catch { toast.error('Failed'); } }}><Send className="w-3 h-3" /></Button></div> : <div className="flex gap-1"><Button variant="outline" size="sm" className="text-blue-600 border-blue-300" onClick={async () => { try { const res = await meetingsAPI.generateLink(m.id); toast.success('Link generated!'); loadData(); window.open(res.data.video_link, '_blank'); } catch (err) { toast.error(err.response?.data?.detail || 'Could not generate link'); setManualLinkMeetingId(m.id); } }}><Video className="w-4 h-4 mr-1" />Generate</Button><Button variant="ghost" size="sm" onClick={() => setManualLinkMeetingId(m.id)} title="Paste link manually"><Send className="w-3 h-3" /></Button></div>)}<Button variant="ghost" size="sm" onClick={() => deleteMeeting(m.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
